// Edición de textos sobre el preview. Cuando el deck corre como preview del borrador dentro
// del editor del backoffice (iframe), un click sobre un texto lo vuelve editable ahí mismo y,
// al confirmar, se le avisa al editor para que lo guarde en el borrador.
//
// No toca las slides: usa el registro de mensajes resueltos de la i18n (MessageTrace) para
// saber qué clave corresponde a cada texto que se ve en pantalla. Solo se ofrecen los textos
// que se pueden identificar sin ambigüedad: un nodo de texto que es el único hijo de su
// elemento y cuyo contenido coincide con un solo mensaje del deck. Lo demás (textos con
// interpolación, con formato, partidos por la slide) se sigue editando desde el backoffice.
//
// Protocolo (postMessage con el parent):
//   deck -> editor  { source: "sota-decks", type: "ready" }
//   editor -> deck  { source: "sota-backoffice", type: "enable-text-editing" }
//   deck -> editor  { source: "sota-decks", type: "text-edited", locale, key, value }
//   deck -> editor  { source: "sota-decks", type: "slide-changed", slideId }
// `key` es relativa al deck (`slides.portada.titulo`), igual que en DeckConfig.messages.

import { useEffect, useRef, useState, type RefObject } from "react";
import { useApplyMessageEdit, useLocale, useMessageTrace } from "@/i18n";

const EDIT_ATTR = "data-edit-key";
const EDITING_ATTR = "data-editing";
// Tras cerrar una edición con un click afuera, ese mismo click no debe cambiar de slide.
const SWALLOW_CLICK_MS = 400;

/** El deck corre como preview del borrador dentro de otro documento (el editor). */
export function isEmbeddedPreview(preview: string | undefined): boolean {
  return Boolean(preview) && typeof window !== "undefined" && window.parent !== window;
}

const normalize = (text: string) => text.replace(/\s+/g, " ").trim();

interface Editing {
  el: HTMLElement;
  /** nodo de texto que React administra; se repone al terminar para no desincronizarlo. */
  node: Text;
  original: string;
  key: string;
}

export function usePreviewTextEditing({
  deckId,
  slideId,
  containerRef,
}: {
  deckId: string;
  slideId: string;
  /** contenedor de las slides (no de los controles del visor). */
  containerRef: RefObject<HTMLElement | null>;
}) {
  const trace = useMessageTrace();
  const applyEdit = useApplyMessageEdit();
  const locale = useLocale();
  // Origen del editor, tomado de su respuesta al "ready": hasta entonces no hay edición.
  const [editorOrigin, setEditorOrigin] = useState<string | null>(null);

  // El registro se vacía al cambiar de slide, antes de que la nueva se pinte: si no, un texto
  // suyo sin clave propia (con formato, interpolado o fijo en el código) podría coincidir con
  // un mensaje de una slide ya visitada y la edición se guardaría en esa otra.
  const [tracedSlide, setTracedSlide] = useState(slideId);
  if (tracedSlide !== slideId) {
    setTracedSlide(slideId);
    trace?.clear();
  }

  useEffect(() => {
    if (!trace) return;
    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent) return;
      const data = event.data as { source?: unknown; type?: unknown } | null;
      if (data?.source !== "sota-backoffice" || data.type !== "enable-text-editing") return;
      setEditorOrigin(event.origin);
    }
    window.addEventListener("message", onMessage);
    // "*": todavía no se conoce el origen del editor y el aviso no lleva datos.
    window.parent.postMessage({ source: "sota-decks", type: "ready" }, "*");
    return () => window.removeEventListener("message", onMessage);
  }, [trace]);

  // Avisa los cambios de slide hechos dentro del preview, para que el editor siga la
  // selección. La slide en la que arranca no se avisa: la eligió el editor.
  // Los cambios que pidió el propio editor (llegan por hash) no se le devuelven: el eco
  // llegaría tarde y, si mientras tanto eligió otra slide, lo haría volver atrás.
  const reportedSlideRef = useRef<string | null>(null);
  const requestedSlideRef = useRef<string | null>(null);
  useEffect(() => {
    if (!trace) return;
    function onHashChange() {
      requestedSlideRef.current = decodeURIComponent(window.location.hash.slice(1));
    }
    // Captura: tiene que correr antes que el listener de navegación, que al despachar el
    // cambio puede dejar ya corrido el efecto de abajo.
    window.addEventListener("hashchange", onHashChange, true);
    return () => window.removeEventListener("hashchange", onHashChange, true);
  }, [trace]);
  useEffect(() => {
    const requested = requestedSlideRef.current;
    requestedSlideRef.current = null;
    if (!editorOrigin) return;
    if (
      reportedSlideRef.current !== null &&
      reportedSlideRef.current !== slideId &&
      requested !== slideId
    ) {
      window.parent.postMessage(
        { source: "sota-decks", type: "slide-changed", slideId },
        editorOrigin,
      );
    }
    reportedSlideRef.current = slideId;
  }, [editorOrigin, slideId]);

  useEffect(() => {
    const container = containerRef.current;
    if (!editorOrigin || !trace || !container) return;
    const origin = editorOrigin;
    const root = container;
    const messages = trace;

    const deckPrefix = `deck.${deckId}.`;
    const slidePrefix = `${deckPrefix}slides.${slideId}.`;
    let editing: Editing | null = null;
    let finishedAt = 0;

    function mark() {
      if (editing) return;
      const pathsByText = new Map<string, string[]>();
      for (const [path, value] of messages) {
        if (!path.startsWith(deckPrefix)) continue;
        const text = normalize(value);
        if (!text) continue;
        const paths = pathsByText.get(text);
        if (paths) paths.push(path);
        else pathsByText.set(text, [path]);
      }

      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const el = node.parentElement;
        if (!el || el.childNodes.length !== 1) continue;
        const paths = pathsByText.get(normalize((node as Text).data)) ?? [];
        // El mismo texto puede repetirse entre slides: gana el de la slide actual.
        const own = paths.filter((path) => path.startsWith(slidePrefix));
        const path = own.length === 1 ? own[0] : paths.length === 1 ? paths[0] : null;
        if (path) el.setAttribute(EDIT_ATTR, path.slice(deckPrefix.length));
        else el.removeAttribute(EDIT_ATTR);
      }
    }

    function editableFrom(target: EventTarget | null): HTMLElement | null {
      if (!(target instanceof Element)) return null;
      return target.closest<HTMLElement>(`[${EDIT_ATTR}]`);
    }

    function finish(commit: boolean) {
      if (!editing) return;
      const { el, node, original, key } = editing;
      editing = null;
      finishedAt = performance.now();

      const typed = (el.textContent ?? "").replace(/\u00a0/g, " ").replace(/\n$/, "");
      const value = commit ? typed.trim() : original;

      el.removeAttribute("contenteditable");
      el.removeAttribute(EDITING_ATTR);
      // El navegador pudo reemplazar el nodo al escribir: se repone el de React. Vacío
      // significa "volver al texto original del deck", que llega con el re-render.
      node.data = value === "" ? original : value;
      el.replaceChildren(node);
      el.blur();

      if (commit && value !== original.trim()) {
        applyEdit(`${deckPrefix}${key}`, value);
        window.parent.postMessage(
          { source: "sota-decks", type: "text-edited", locale, key, value },
          origin,
        );
      }
    }

    // mousedown (no click): al volverlo editable antes de la acción por defecto, el cursor
    // queda donde se hizo click.
    function onMouseDown(event: MouseEvent) {
      if (editing || event.button !== 0) return;
      const el = editableFrom(event.target);
      const key = el?.getAttribute(EDIT_ATTR);
      const node = el?.firstChild;
      if (!el || !key || el.childNodes.length !== 1 || !(node instanceof Text)) return;
      editing = { el, node, original: node.data, key };
      el.setAttribute("contenteditable", "plaintext-only");
      // Navegadores sin "plaintext-only": sin esto el texto no queda editable ni toma el
      // foco, la edición nunca termina y el preview deja de responder a los clicks.
      if (!el.isContentEditable) el.setAttribute("contenteditable", "true");
      el.setAttribute(EDITING_ATTR, "");
      requestAnimationFrame(() => {
        if (editing?.el === el && document.activeElement !== el) el.focus();
      });
    }

    // Fase de captura: frena el click antes de que el visor lo tome como "siguiente slide".
    function onClick(event: MouseEvent) {
      const justFinished = performance.now() - finishedAt < SWALLOW_CLICK_MS;
      if (editing || justFinished || editableFrom(event.target)) {
        event.stopPropagation();
        event.preventDefault();
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (!editing) return;
      // Nada de lo que se escribe debe llegar a los atajos del visor (flechas, F, N…).
      event.stopPropagation();
      if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
        event.preventDefault();
        finish(true);
      } else if (event.key === "Escape") {
        event.preventDefault();
        finish(false);
      }
    }

    function onFocusOut(event: FocusEvent) {
      if (editing && event.target === editing.el) finish(true);
    }

    let pending: ReturnType<typeof setTimeout> | undefined;
    const observer = new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(mark, 120);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    mark();

    root.addEventListener("mousedown", onMouseDown, true);
    root.addEventListener("click", onClick, true);
    root.addEventListener("keydown", onKeyDown, true);
    root.addEventListener("focusout", onFocusOut);

    return () => {
      // Cambiar de slide con una edición abierta la confirma, igual que hacer click afuera.
      finish(true);
      clearTimeout(pending);
      observer.disconnect();
      root.removeEventListener("mousedown", onMouseDown, true);
      root.removeEventListener("click", onClick, true);
      root.removeEventListener("keydown", onKeyDown, true);
      root.removeEventListener("focusout", onFocusOut);
      for (const el of root.querySelectorAll(`[${EDIT_ATTR}]`)) el.removeAttribute(EDIT_ATTR);
    };
  }, [editorOrigin, trace, applyEdit, containerRef, deckId, slideId, locale]);
}
